/**
 * Centralized logging utility for TempStars Admin Dashboard
 *
 * Usage:
 * import { logger } from '@/utils/logger';
 * logger.debug('User action', { userId: 123 });
 * logger.error('API failed', error);
 *
 * In production, debug/info logs are suppressed to improve performance.
 * Errors and warnings are always logged and can be sent to monitoring services.
 */

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  data?: any;
  stack?: string;
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === "development";
  private isTest = process.env.NODE_ENV === "test";

  /**
   * Debug level logging - development only
   * Use for detailed debugging information during development
   */
  debug(message: string, data?: any): void {
    if (this.isDevelopment && !this.isTest) {
      console.log(`🔍 [DEBUG] ${message}`, data || "");
    }
  }

  /**
   * Info level logging - development only
   * Use for general informational messages
   */
  info(message: string, data?: any): void {
    if (this.isDevelopment && !this.isTest) {
      console.info(`ℹ️  [INFO] ${message}`, data || "");
    }
  }

  /**
   * Warning level logging - development and production
   * Use for potentially harmful situations that don't prevent operation
   */
  warn(message: string, data?: any): void {
    if (this.isDevelopment) {
      console.warn(`⚠️  [WARN] ${message}`, data || "");
    } else if (!this.isTest) {
      // In production, send to monitoring service
      this.sendToMonitoring("warn", message, data);
    }
  }

  /**
   * Error level logging - always logged
   * Use for error conditions that need immediate attention
   */
  error(message: string, error?: Error | any, context?: any): void {
    const logEntry: LogEntry = {
      level: "error",
      message,
      timestamp: new Date().toISOString(),
      data: context,
      stack: error?.stack
    };

    if (this.isDevelopment) {
      console.error(`❌ [ERROR] ${message}`, error, context);
    } else if (!this.isTest) {
      // In production, send to error tracking service (Sentry, CloudWatch, etc.)
      this.sendToMonitoring("error", message, { error, context });
    }
  }

  /**
   * Performance timing - development only
   * Use to measure performance of operations
   */
  time(label: string): void {
    if (this.isDevelopment && !this.isTest) {
      console.time(`⏱️  ${label}`);
    }
  }

  /**
   * End performance timing
   */
  timeEnd(label: string): void {
    if (this.isDevelopment && !this.isTest) {
      console.timeEnd(`⏱️  ${label}`);
    }
  }

  /**
   * Group related log messages
   */
  group(label: string): void {
    if (this.isDevelopment && !this.isTest) {
      console.group(`📁 ${label}`);
    }
  }

  /**
   * End log group
   */
  groupEnd(): void {
    if (this.isDevelopment && !this.isTest) {
      console.groupEnd();
    }
  }

  /**
   * Send logs to monitoring service
   * TODO: Implement integration with your monitoring service
   * Examples:
   * - Sentry: Sentry.captureMessage(message, level);
   * - CloudWatch: cloudwatch.putLogEvents(...);
   * - Custom: fetch('/api/logs', { method: 'POST', body: JSON.stringify({ level, message, data }) });
   */
  private sendToMonitoring(level: LogLevel, message: string, data?: any): void {
    // TODO: Implement integration with monitoring service
    // For now, we'll just suppress the logs in production
    // This prevents console pollution while maintaining a clean API
    // Example implementation:
    // if (level === 'error') {
    //   Sentry.captureException(data?.error || new Error(message), {
    //     level: 'error',
    //     extra: data
    //   });
    // } else {
    //   Sentry.captureMessage(message, level as Sentry.SeverityLevel);
    // }
  }

  /**
   * Log a table of data (development only)
   * Useful for displaying arrays of objects
   */
  table(data: any[]): void {
    if (this.isDevelopment && !this.isTest) {
      console.table(data);
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Re-export for convenience
export default logger;
